import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { formatGs, LOCATION_NAMES, formatDate } from "@/lib/format";
import { Modal } from "@/components/ui/kit";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { VoidBadge, VoidRequestButton } from "@/components/VoidActions";

const Stat = ({ l, v, cls = "", tid }) => (
  <div className="rounded-lg bg-secondary p-3"><p className="text-[10px] font-bold tracking-widest text-muted-foreground">{l}</p><p className={`font-data font-bold ${cls}`} data-testid={tid}>{v}</p></div>
);
const diffCls = (d) => (d === 0 ? "text-emerald-500" : d < 0 ? "text-red-500" : "text-amber-500");

export const MyShiftModal = ({ open, onClose, location, cash, onReprint }) => {
  const [sales, setSales] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tab, setTab] = useState("turno");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      cash?.open ? api.get("/sales", { params: { location, mine: true, session_id: cash.id } }) : Promise.resolve({ data: [] }),
      api.get("/cash/sessions", { params: { location, mine: true } }),
    ]).then(([s, c]) => { setSales(s.data); setSessions(c.data.filter((x) => x.status === "cerrada")); }).catch((e) => toast.error(apiErr(e)));
  }, [open, location, cash, tick]);
  useEffect(() => { if (open) setTab("turno"); }, [open]);

  const m = cash?.by_method || { efectivo: 0, tarjeta: 0, transferencia: 0 };
  return (
    <Modal open={open} onClose={onClose} title={`Mi caja · ${LOCATION_NAMES[location]}`} wide top>
      <div className="flex gap-2 mb-4">
        {[["turno", "Turno actual"], ["cierres", "Mis cierres"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`myshift-tab-${k}`} className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${tab === k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{l}</button>
        ))}
      </div>
      {tab === "turno" && (!cash?.open ? <p className="text-sm text-muted-foreground text-center py-8">No tenés una caja abierta</p> : (
        <div className="space-y-4" data-testid="myshift-current">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat l="VENTAS" v={`${cash.num_sales} · ${formatGs(cash.total_sales)}`} cls="text-primary text-sm whitespace-nowrap" tid="myshift-total" />
            <Stat l="EFECTIVO" v={formatGs(m.efectivo)} cls="text-emerald-500" />
            <Stat l="POS" v={formatGs(m.tarjeta)} cls="text-blue-500" />
            <Stat l="TRANSFER" v={formatGs(m.transferencia)} cls="text-purple-500" />
          </div>
          <p className="text-xs text-muted-foreground">Abierta {formatDate(cash.opened_at)} · Inicial {formatGs(cash.opening_amount)} · Efectivo esperado <b className="text-foreground">{formatGs(cash.expected_cash)}</b></p>
          <div className="space-y-1.5 max-h-[40vh] overflow-y-auto" data-testid="myshift-sales">
            {sales.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sin ventas en este turno</p>}
            {sales.map((s) => (
              <div key={s.id} className="flex items-center gap-3 bg-secondary rounded-lg px-3 py-2 text-sm">
                <span className="text-xs text-muted-foreground w-16 whitespace-nowrap">{new Date(s.timestamp).toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" })}</span>
                <span className="flex-1 truncate">{s.items.map((i) => `${i.quantity} ${i.name}`).join(", ")}</span>
                <span className="text-[10px] uppercase text-muted-foreground">{s.payment_method}</span>
                <span className={`font-data font-bold ${s.status === "anulada" ? "line-through text-muted-foreground" : ""}`}>{formatGs(s.total)}</span>
                <VoidBadge sale={s} />
                <VoidRequestButton sale={s} onDone={() => setTick((t) => t + 1)} />
                <button onClick={() => onReprint(s)} title="Reimprimir" data-testid={`myshift-reprint-${s.id}`} className="text-muted-foreground hover:text-primary"><Printer className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {tab === "cierres" && (
        <div className="space-y-1.5 max-h-[55vh] overflow-y-auto" data-testid="myshift-closes">
          {sessions.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Todavía no cerraste cajas</p>}
          {sessions.map((s) => (
            <div key={s.id} className="bg-secondary rounded-lg px-3 py-2 text-sm">
              <div className="flex justify-between"><span className="font-medium">{formatDate(s.closed_at)}{s.handover_to && <span className="ml-2 text-[10px] font-bold text-primary">TRASPASO → {s.handover_to}</span>}{s.handover_from && <span className="ml-2 text-[10px] font-bold text-primary">RECIBIDA DE {s.handover_from}</span>}</span><span className={`font-data font-bold ${diffCls(s.difference)}`}>Dif. {formatGs(s.difference)}</span></div>
              <p className="text-xs text-muted-foreground">Inicial {formatGs(s.opening_amount)} · Ventas {s.summary?.num_sales ?? 0} ({formatGs(s.summary?.total_sales ?? 0)}) · Esperado {formatGs(s.expected_cash)} · Contado {formatGs(s.counted_amount)}</p>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
